import { NgClass } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AuthService } from '@oneComply/core/services/auth.service';
import { HSStaticMethods } from 'preline/preline';
import { filter } from 'rxjs';

import { AdminHeader } from './components/admin-header/admin-header';
import { AdminPreloader } from './components/admin-preloader/admin-preloader';
import { AdminSidebar } from './components/admin-sidebar/admin-sidebar';
import {
  shouldShowAdminShell,
  shouldShowAuthRedirectPreloader,
} from './utils/admin-shell.util';

declare const HSOverlay: { autoInit: () => void } | undefined;

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NgClass, AdminSidebar, AdminHeader, AdminPreloader],
  templateUrl: './app.html',
})
export class App implements OnInit, AfterViewInit {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);

  protected readonly isSidebarOpen = signal(false);
  protected readonly showAdminShell = signal(this.resolveShowAdminShell());
  protected readonly showAuthRedirectPreloader = signal(this.resolveAuthRedirectPreloader());

  ngOnInit(): void {
    this.isSidebarOpen.set(this.shouldSidebarStartOpen());
    this.removeBootSplash();
    this.updateShellVisibility();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.updateShellVisibility();
        this.initializePreline();
      });
  }

  ngAfterViewInit(): void {
    this.initializePreline();
  }

  protected onToggleSidebar(): void {
    this.isSidebarOpen.update((open) => !open);

    if (!this.isSidebarOpen()) {
      this.releaseSidebarFocus();
    }
  }

  protected onCloseSidebar(): void {
    this.isSidebarOpen.set(false);
    this.releaseSidebarFocus();
  }

  protected onLogout(): void {
    this.authService.logout();
  }

  private resolveShowAdminShell(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return shouldShowAdminShell(window.location.pathname, this.authService.getToken() !== null);
  }

  private resolveAuthRedirectPreloader(): boolean {
    if (typeof window === 'undefined') {
      return true;
    }

    return shouldShowAuthRedirectPreloader(
      window.location.pathname,
      this.resolveShowAdminShell(),
    );
  }

  private updateShellVisibility(): void {
    const path = this.router.url.split('?')[0] || '/';
    const showShell = shouldShowAdminShell(path, this.authService.getToken() !== null);

    this.showAdminShell.set(showShell);
    this.showAuthRedirectPreloader.set(shouldShowAuthRedirectPreloader(path, showShell));
  }

  private removeBootSplash(): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.getElementById('admin-boot-splash')?.remove();
  }

  private shouldSidebarStartOpen(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia('(min-width: 1024px)').matches;
  }

  private initializePreline(): void {
    if (typeof window === 'undefined') {
      return;
    }

    requestAnimationFrame(() => {
      HSStaticMethods.cleanCollection();
      HSStaticMethods.autoInit();
      HSOverlay?.autoInit();
    });
  }

  private releaseSidebarFocus(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const activeElement = document.activeElement;
    const sidebarElement = document.getElementById('admin-sidebar');

    if (activeElement instanceof HTMLElement && sidebarElement?.contains(activeElement)) {
      activeElement.blur();
    }
  }
}

